export default function ModuleSummary() {
  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-br from-[#1A3A2E] to-[#0D1F17] rounded-2xl p-10 text-white">
        <div className="flex items-center gap-3 mb-6">
          <i className="ri-file-list-3-line text-3xl text-[#39FF14]"></i>
          <h2 className="text-3xl font-bold">Resumen Ejecutivo del Módulo 1</h2>
        </div>
        <p className="text-lg text-gray-300 leading-relaxed mb-8">
          Has completado los fundamentos de anatomía funcional y biomecánica aplicada al entrenamiento. 
          Estos conceptos son la base para analizar cualquier ejercicio y diseñar programas efectivos.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-8 border border-gray-200">
          <div className="w-12 h-12 bg-[#39FF14] rounded-full flex items-center justify-center mb-4">
            <span className="text-2xl">🎯</span>
          </div>
          <h3 className="text-xl font-bold text-black mb-4">Conceptos Clave Dominados</h3>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <i className="ri-check-line text-[#39FF14] text-xl flex-shrink-0 mt-0.5"></i>
              <span className="text-sm text-gray-700 leading-relaxed">
                <strong>Planos y ejes:</strong> Todo movimiento ocurre en planos tridimensionales. Entrena en los tres para desarrollo equilibrado.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <i className="ri-check-line text-[#39FF14] text-xl flex-shrink-0 mt-0.5"></i>
              <span className="text-sm text-gray-700 leading-relaxed">
                <strong>Articulaciones:</strong> Las móviles (hombro, cadera) necesitan estabilización. Las estables (codo, rodilla) toleran más carga.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <i className="ri-check-line text-[#39FF14] text-xl flex-shrink-0 mt-0.5"></i>
              <span className="text-sm text-gray-700 leading-relaxed">
                <strong>Palancas:</strong> La mayoría de movimientos son de tercer grado: sacrifican eficiencia por velocidad y rango.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <i className="ri-check-line text-[#39FF14] text-xl flex-shrink-0 mt-0.5"></i>
              <span className="text-sm text-gray-700 leading-relaxed">
                <strong>Torque:</strong> La dificultad real de un ejercicio viene del brazo de momento, no solo del peso.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <i className="ri-check-line text-[#39FF14] text-xl flex-shrink-0 mt-0.5"></i>
              <span className="text-sm text-gray-700 leading-relaxed">
                <strong>Curvas de fuerza:</strong> Combina ejercicios con diferentes curvas para estímulo completo.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <i className="ri-check-line text-[#39FF14] text-xl flex-shrink-0 mt-0.5"></i>
              <span className="text-sm text-gray-700 leading-relaxed">
                <strong>Ejercicios clave:</strong> La técnica debe individualizarse según anatomía y objetivo.
              </span>
            </li>
          </ul>
        </div>

        <div className="bg-[#F5F2EB] rounded-2xl p-8">
          <div className="w-12 h-12 bg-[#1A3A2E] rounded-full flex items-center justify-center mb-4">
            <span className="text-2xl">💡</span>
          </div>
          <h3 className="text-xl font-bold text-black mb-4">Aplicación Práctica Inmediata</h3>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <i className="ri-arrow-right-s-line text-[#1A3A2E] text-xl flex-shrink-0 mt-0.5"></i>
              <span className="text-sm text-gray-700 leading-relaxed">
                Analiza cada ejercicio identificando: plano de movimiento, tipo de articulación, tipo de palanca, punto de máximo torque.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <i className="ri-arrow-right-s-line text-[#1A3A2E] text-xl flex-shrink-0 mt-0.5"></i>
              <span className="text-sm text-gray-700 leading-relaxed">
                Modifica ejercicios cambiando ángulos corporales para ajustar el brazo de momento y la dificultad.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <i className="ri-arrow-right-s-line text-[#1A3A2E] text-xl flex-shrink-0 mt-0.5"></i>
              <span className="text-sm text-gray-700 leading-relaxed">
                Combina ejercicios compuestos (curva ascendente) con aislados (curva descendente) para estímulo completo.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <i className="ri-arrow-right-s-line text-[#1A3A2E] text-xl flex-shrink-0 mt-0.5"></i>
              <span className="text-sm text-gray-700 leading-relaxed">
                Individualiza la técnica según anatomía: no existe una técnica única perfecta para todos.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <i className="ri-arrow-right-s-line text-[#1A3A2E] text-xl flex-shrink-0 mt-0.5"></i>
              <span className="text-sm text-gray-700 leading-relaxed">
                Si un ejercicio causa dolor, analiza la biomecánica antes de descartarlo: puede ser un problema de técnica o movilidad.
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-8 border-2 border-[#39FF14]">
        <h3 className="text-2xl font-bold text-black mb-6">Errores Críticos a Evitar</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-close-line text-red-600 text-xl"></i>
            </div>
            <div>
              <h4 className="font-bold text-black mb-2">Entrenar solo en plano sagital</h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                Descuidar movimientos laterales y rotacionales crea desequilibrios y aumenta el riesgo de lesión.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-close-line text-red-600 text-xl"></i>
            </div>
            <div>
              <h4 className="font-bold text-black mb-2">Forzar ROM extremo bajo carga</h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                Especialmente en articulaciones móviles. Gana movilidad primero, luego carga el rango que controlas.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-close-line text-red-600 text-xl"></i>
            </div>
            <div>
              <h4 className="font-bold text-black mb-2">Usar momentum en el punto crítico</h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                Rebotar o usar impulso en el punto de máximo torque elimina el estímulo justo donde más se necesita.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-close-line text-red-600 text-xl"></i>
            </div>
            <div>
              <h4 className="font-bold text-black mb-2">Técnica dogmática universal</h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                La técnica óptima varía según anatomía individual. Aplica principios, no recetas rígidas.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-[#39FF14] to-[#2EE000] rounded-2xl p-8">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-2xl font-bold text-black mb-2">¿Listo para el siguiente paso?</h3>
            <p className="text-black/80">
              Continúa con el Módulo 2: Fisiología del Ejercicio y Adaptaciones
            </p>
          </div>
          <button className="bg-black text-white px-8 py-4 rounded-full font-medium hover:bg-[#1A3A2E] transition-colors whitespace-nowrap cursor-pointer">
            Siguiente Módulo
          </button>
        </div>
      </div>
    </div>
  );
}